import { enableAllPlugins, produce } from 'immer';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import has from 'lodash/has';
import FileRequestAPI from '../../../../api/FileRequest';
import ItemAPI from '../../../../api/Item';

import readFileAsDataURL from '../../../../common/utils/readFileAsDataURL';
import {
  ACCEPTED_IMG_TYPES,
  FILE_REQUEST_STATUS,
  MAX_CUSTOM_LOGO_SIZE,
  MAX_NUMBER_OF_FIELDS,
  MODE,
} from '../../../../constants';

import builderNotificationMessages from '../../../new-file-request-builder/builder-notification/messages';
import {
  addCheckboxField,
  addDescriptionField,
  addEmailField,
  addMetadataField,
  addRadioField,
  addUploadField,
  removeField,
  toggleFieldRequired,
} from '../../../new-file-request-builder/FormElementMaker';
import {
  getFormElementKey,
  getLayoutAfterFieldColToggle,
  isItemInTwoColumn,
  TOTAL_BORDER_WIDTH,
  updateTwoColItemHeightInLayout,
} from '../../../new-file-request-builder/grid-layout-utils';
import {
  ACTION_ADD_CHECKBOX_FIELD,
  ACTION_ADD_DESCRIPTION_FIELD,
  ACTION_ADD_EMAIL_FIELD,
  ACTION_ADD_FIELD,
  ACTION_ADD_METADATA_FIELD,
  ACTION_ADD_RADIO_FIELD,
  ACTION_ADD_UPLOAD_FIELD,
  ACTION_ADD_UPLOAD_FIELD_TO_METADATA_FIELD,
  ACTION_ALREADY_SAVED,
  ACTION_CLEAR_CONTENT_PICKER,
  ACTION_CLEAR_FORM_UPLOAD_FOLDER,
  ACTION_CLEAR_METADATA_TEMPLATE,
  ACTION_CLOSE_CLEAR_METADATA_TEMPLATE_MODAL,
  ACTION_CLOSE_COPY_MODAL,
  ACTION_CLOSE_DELETE_FILE_REQUEST_MODAL,
  ACTION_CLOSE_EXIT_MODAL,
  ACTION_CLOSE_FIELD_SELECTOR_DRAWER,
  ACTION_CLOSE_METADATA_PICKER,
  ACTION_CLOSE_NOTIFICATION,
  ACTION_CLOSE_SETTINGS_PANEL,
  ACTION_CLOSE_SHARE_MODAL,
  ACTION_CLOSE_THEME_PANEL,
  ACTION_CLOSE_UPLOAD_LOGO_MODAL,
  ACTION_COLOR_PICKER_ADD,
  ACTION_COLOR_PICKER_CANCEL,
  ACTION_COLOR_PICKER_CHANGE,
  ACTION_COLOR_PICKER_ERROR,
  ACTION_COPY_FILE_REQUEST,
  ACTION_COPY_FILE_REQUEST_IN_PROGRESS,
  ACTION_CREATE_FORM_AND_PREVIEW,
  ACTION_CREATE_FORM_AND_SAVE,
  ACTION_CREATE_FORM_AND_SHARE,
  ACTION_DEACTIVATE_BAD_FILE_REQUEST,
  ACTION_DELETE_FILE_REQUEST,
  ACTION_DRAG_FORM_FIELD_END,
  ACTION_DRAG_FORM_FIELD_ENTER_DROPZONE,
  ACTION_DRAG_FORM_FIELD_LEFT_DROPZONE,
  ACTION_DRAG_FORM_FIELD_START,
  ACTION_DRAGGING_FINISHED,
  ACTION_DRAGGING_STARTED,
  ACTION_FILE_REQUEST_UPDATED_FROM_SHARE_MODAL,
  ACTION_GET_FOLDER_TREE,
  ACTION_GET_FOLDER_TREE_IN_PROGRESS,
  ACTION_HAS_SCROLLED_TO_BOTTOM,
  ACTION_HEADER_LOGO_SELECT,
  ACTION_HIDE_METADATA_FIELDS,
  ACTION_NOTIFY_FILE_REQUEST_INACTIVE,
  ACTION_NOTIFY_METADATA_TEMPLATE_ERROR,
  ACTION_ON_FOCUS_ELEMENT,
  ACTION_ON_HELP,
  ACTION_ON_UNFOCUS_ELEMENT,
  ACTION_OPEN_CLEAR_METADATA_TEMPLATE_MODAL,
  ACTION_OPEN_COPY_MODAL,
  ACTION_OPEN_DELETE_FILE_REQUEST_MODAL,
  ACTION_OPEN_EXIT_MODAL,
  ACTION_OPEN_FIELD_SELECTOR_DRAWER,
  ACTION_OPEN_METADATA_PICKER,
  ACTION_OPEN_SELECT_MODAL,
  ACTION_OPEN_SETTINGS_PANEL,
  ACTION_OPEN_SHARE_MODAL,
  ACTION_OPEN_THEME_PANEL,
  ACTION_OPEN_UPLOAD_LOGO_MODAL,
  ACTION_REMOVE_DESCRIPTION,
  ACTION_REMOVE_FIELD,
  ACTION_REMOVE_UPLOAD_FIELD_FROM_METADATA_FIELD,
  ACTION_REORDER_GROUP_FIELDS,
  ACTION_RESET_SAVE_BEFORE_PREVIEW_IN_PROGRESS,
  ACTION_RESPONSE_ERROR_ON_SAVE,
  ACTION_SAVE,
  ACTION_SAVE_AND_COPY,
  ACTION_SAVE_AND_EXIT,
  ACTION_SAVE_AND_PREVIEW,
  ACTION_SAVE_BEFORE_PREVIEW_IN_PROGRESS,
  ACTION_SAVE_BEFORE_SHARE_IN_PROGRESS,
  ACTION_SAVE_IN_PROGRESS,
  ACTION_SEARCH_ITEMS,
  ACTION_SEARCH_ITEMS_IN_PROGRESS,
  ACTION_SELECT_FORM_UPLOAD_FOLDER,
  ACTION_SELECT_METADATA,
  ACTION_SELECT_METADATA_TEMPLATE,
  ACTION_SELECT_THEME_COLOR,
  ACTION_SET_BUILDER_ERROR,
  ACTION_SET_DESCRIPTION,
  ACTION_SET_DESCRIPTION_EDITABLE,
  ACTION_SET_FORM_ELEMENT_TESTIDS,
  ACTION_SET_LABEL,
  ACTION_SET_LIST_OPTIONS_ERROR,
  ACTION_SET_UPLOADED_LOGO,
  ACTION_SHOW_COLOR_PICKER,
  ACTION_SHOW_METADATA_FIELDS,
  ACTION_STORE_FORM_ELEMENT_HEIGHT,
  ACTION_STORE_INSERT_INDEX,
  ACTION_TOGGLE_FIELD_COLUMN,
  ACTION_TOGGLE_FILE_REQUEST_ACTIVITY,
  ACTION_TOGGLE_REQUIRED_FIELD,
  ACTION_TOGGLE_SHOW_UPLOAD_FILE_DESCRIPTION,
  ACTION_TOOLBAR_DROPDOWN_CLOSE,
  ACTION_TOOLBAR_DROPDOWN_OPEN,
  ACTION_UPDATE_GROUP_LAYOUT,
  ACTION_UPDATE_ITEM_HEIGHT_IN_LAYOUT,
  ACTION_UPDATE_LIST_OPTIONS,
  ACTION_UPDATE_MAXIMUM_SELECTIONS,
  ACTION_UPDATE_UPLOAD_LOGO_ITEM,
  ACTION_UPLOAD_LOGO,
  ACTION_UPLOAD_LOGO_CLEAR,
  ACTION_UPLOAD_LOGO_EMPTY,
  ACTION_UPLOAD_LOGO_ERROR,
  ACTION_UPLOAD_LOGO_IN_PROGRESS,
  ACTION_UPLOAD_LOGO_IS_NOT_IMAGE,
  ACTION_UPLOAD_LOGO_TOO_LARGE,
  ACTION_VALIDATION_ERROR_ON_SAVE,
  FORMS_BUILDER_PREVIEW_WINDOW,
} from '../builder-constant';
import selector from '../builder-selector';
import type { Action, State } from '../builder-type';
import { calculateNewMaxSelections, willExceedMaxFields } from '../utils';
import type { onDeleteType } from './action-type';
import {
  actionCreateFormAndSave,
  actionSave,
  actionToggleFileRequestActivity,
  openPreviewWindow,
  recordBuilderHeaderAnalytics,
  removeFieldFromFormContent,
} from './builder-action.utils';

// Enable all immer plugins
enableAllPlugins();

type BuilderActionArgs = {
  onDelete: onDeleteType;
};

type ActionWithRequiredType = {
  type: string;
} & Partial<Action>;
type Dispatch = (action: ActionWithRequiredType | Action) => void;

export default ({ onDelete }: BuilderActionArgs) => {
  return {
    [ACTION_OPEN_METADATA_PICKER]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const state = getState();

        if (willExceedMaxFields(state)) {
          dispatch({
            type: ACTION_SET_BUILDER_ERROR,
            errorMessage: {
              ...builderNotificationMessages.errorMaxFieldsNotification,
              // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
              values: { maxCount: MAX_NUMBER_OF_FIELDS },
            },
          });
          return;
        }

        dispatch(action);
      },
    [ACTION_CLOSE_METADATA_PICKER]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    // Figure out where this is being used? I don't think this is being used anymore
    // ACTION_INSERT_METADATA is used instead and this should be deprecated
    [ACTION_SELECT_METADATA_TEMPLATE]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const { newMetadataTemplate } = action;
        const state = getState();
        let formContent = selector.getFormContent(state);
        forEach(
          filter(
            newMetadataTemplate.fields,
            field => field.isSelected === true,
          ),
          metadataField => {
            formContent = addMetadataField(
              metadataField,
              newMetadataTemplate.id,
              formContent,
            );
          },
        );
        dispatch({ ...action, formContent });
      },
    [ACTION_OPEN_CLEAR_METADATA_TEMPLATE_MODAL]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_CLOSE_CLEAR_METADATA_TEMPLATE_MODAL]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_CLEAR_METADATA_TEMPLATE]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const state = getState();
        const { resin, resinEvent } = state;
        let formContent = selector.getFormContent(state);
        forEach(
          filter(formContent.components, component =>
            has(component, 'metadataTemplateId'),
          ),
          component => {
            formContent = removeField(component.id, formContent);
          },
        );
        resin.recordAction({
          ...resinEvent,
          component: 'formbuilderheader|options',
          target: 'cleartemplate',
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
        dispatch({ ...action, formContent });
      },
    [ACTION_ADD_UPLOAD_FIELD_TO_METADATA_FIELD]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_REMOVE_UPLOAD_FIELD_FROM_METADATA_FIELD]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_OPEN_FIELD_SELECTOR_DRAWER]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const state = getState();
        const { resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formbuilder',
          target: 'addfield',
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
        dispatch(action);
      },
    [ACTION_CLOSE_FIELD_SELECTOR_DRAWER]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_SELECT_METADATA]:
      (dispatch: Dispatch, getState: () => State) => () => {
        const state = getState();
        const { resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formbuilder',
          target: 'addmetadata',
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
      },
    [ACTION_SHOW_METADATA_FIELDS]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_HIDE_METADATA_FIELDS]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_ADD_METADATA_FIELD]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const state = getState();
        const { metadataField, metadataTemplateId } = action;
        let formContent = selector.getFormContent(state);
        formContent = addMetadataField(
          metadataField,
          metadataTemplateId,
          formContent,
        );
        const newMetadataTemplate = produce(
          selector.getActiveMetadataTemplate(state),
          newTemplate => {
            const field = find(get(newTemplate, 'fields', []), [
              'key',
              metadataField.key,
            ]);
            if (field) field.isSelected = true;
          },
        );
        dispatch({ type: ACTION_ADD_FIELD, formContent });
        dispatch({ ...action, newMetadataTemplate });
      },
    [ACTION_ADD_EMAIL_FIELD]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const { intl } = action;
        const state = getState();
        let formContent = selector.getFormContent(state);
        const formType = get(state, 'workingFileRequest.form.formType');
        formContent = addEmailField(formContent, intl, formType);
        const { resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formbuilder',
          target: 'addemail',
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
        dispatch({ type: ACTION_ADD_FIELD, formContent });
        dispatch({ ...action });
      },
    [ACTION_ADD_DESCRIPTION_FIELD]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const { intl } = action;
        const state = getState();
        let formContent = selector.getFormContent(state);
        formContent = addDescriptionField(formContent, intl);
        const { resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formbuilder',
          target: 'addfiledescription',
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
        dispatch({ type: ACTION_ADD_FIELD, formContent });
        dispatch({ ...action });
      },
    [ACTION_ADD_RADIO_FIELD]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const state = getState();
        let formContent = selector.getFormContent(state);
        formContent = addRadioField(formContent);
        const { resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formbuilder',
          target: 'addradio',
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
        dispatch({ type: ACTION_ADD_FIELD, formContent });
        dispatch({ ...action });
      },
    [ACTION_ADD_CHECKBOX_FIELD]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const state = getState();
        let formContent = selector.getFormContent(state);
        formContent = addCheckboxField(formContent);
        const { resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formbuilder',
          target: 'addcheckbox',
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
        dispatch({ type: ACTION_ADD_FIELD, formContent });
        dispatch({ ...action });
      },
    [ACTION_ADD_UPLOAD_FIELD]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const { intl, mode } = action;
        const state = getState();
        let formContent = selector.getFormContent(state);
        formContent = addUploadField(formContent, intl, mode);
        const { resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formbuilder',
          target: 'adduploadzone',
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
        dispatch({ type: ACTION_ADD_FIELD, formContent });
        dispatch({ ...action });
      },
    [ACTION_REMOVE_FIELD]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const { id, intl, multipleMetadataTemplatesEnabled } = action;
        const state = getState();

        // If metadata field, unselect field
        let newMetadataTemplates = cloneDeep(
          selector.getActiveMetadataTemplates(state),
        );

        const component = selector.getComponentForId(state, id);
        if (
          newMetadataTemplates?.length > 0 &&
          has(component, 'metadataFieldKey') &&
          has(component, 'metadataTemplateId')
        ) {
          // find the template for field to remove and set isSelected false for the fields
          const templateToUpdate = find(newMetadataTemplates, {
            id: component.metadataTemplateId,
          });
          if (templateToUpdate) {
            const field = find(templateToUpdate.fields, [
              'key',
              component.metadataFieldKey,
            ]);
            if (field) field.isSelected = false;
            if (
              templateToUpdate.fields.every(({ isSelected }) => !isSelected)
            ) {
              // remove template if all the fields in this template is unselected
              newMetadataTemplates = newMetadataTemplates.filter(
                templateEl => templateEl.id !== templateToUpdate.id,
              );
            }
          }
        }

        // Update testids
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [id]: removedTestid, ...formElementTestids } =
          state.formElementTestids;

        const formContent = removeFieldFromFormContent(state, component, intl);

        const { resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formelement',
          target: `remove`,
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
        dispatch({
          type: ACTION_REMOVE_FIELD,
          formContent,
          newMetadataTemplates,
          multipleMetadataTemplatesEnabled,
        });
        dispatch({ type: ACTION_SET_FORM_ELEMENT_TESTIDS, formElementTestids });
      },
    [ACTION_TOGGLE_REQUIRED_FIELD]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const state = getState();
        const { id } = action;
        let formContent = selector.getFormContent(state);
        formContent = toggleFieldRequired(id, formContent);
        const newRequired = get(
          selector.getComponentForId(state, id),
          'required',
        );
        const { resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formelement',
          target: `markasrequired|${newRequired ? 'check' : 'uncheck'}`,
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
        dispatch({ ...action, formContent });
      },
    [ACTION_OPEN_DELETE_FILE_REQUEST_MODAL]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_CLOSE_DELETE_FILE_REQUEST_MODAL]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_DELETE_FILE_REQUEST]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const { exitBuilder, navigation, baseUrl, mode } = action;
        const state = getState();
        const { workingFileRequest, resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formbuilderheader|options',
          target: 'delete',
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
        FileRequestAPI.deleteFileRequest(workingFileRequest).then(() => {
          dispatch({ type: ACTION_CLOSE_DELETE_FILE_REQUEST_MODAL });
          if (typeof onDelete === 'function') {
            onDelete();
          }

          exitBuilder(baseUrl, mode, state, navigation.applicationHistory);
        });
      },
    [ACTION_ON_HELP]: (dispatch: Dispatch, getState: () => State) => () => {
      const state = getState();
      const { resin, resinEvent } = state;
      resin.recordAction({
        ...resinEvent,
        component: 'formbuilderheader|options',
        target: 'gethelp',
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
    },
    [ACTION_ON_FOCUS_ELEMENT]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_ON_UNFOCUS_ELEMENT]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
      dispatch({ type: ACTION_CLOSE_SETTINGS_PANEL });
    },
    [ACTION_CLOSE_NOTIFICATION]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_NOTIFY_METADATA_TEMPLATE_ERROR]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_DEACTIVATE_BAD_FILE_REQUEST]:
      (dispatch: Dispatch, getState: () => State) => async () => {
        const state = getState();
        if (state.savedFileRequest.status === FILE_REQUEST_STATUS.ACTIVE) {
          await actionToggleFileRequestActivity(dispatch, state);
          dispatch({ type: ACTION_NOTIFY_FILE_REQUEST_INACTIVE });
        }
        dispatch({ type: ACTION_DEACTIVATE_BAD_FILE_REQUEST });
      },
    [ACTION_TOGGLE_FILE_REQUEST_ACTIVITY]:
      (dispatch: Dispatch, getState: () => State) => async () => {
        const state = getState();
        await actionToggleFileRequestActivity(dispatch, state);
      },
    [ACTION_NOTIFY_FILE_REQUEST_INACTIVE]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_VALIDATION_ERROR_ON_SAVE]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_RESPONSE_ERROR_ON_SAVE]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_SAVE]: (dispatch: Dispatch, getState: () => State) => async () => {
      const state = getState();
      const formType = get(state, 'workingFileRequest.form.formType');
      const hasUnsavedChanges = selector.hasUnsavedChanges(state);
      recordBuilderHeaderAnalytics(state, 'save');
      if (formType === MODE.FORM && !hasUnsavedChanges) {
        dispatch({ type: ACTION_ALREADY_SAVED });
        return;
      }
      dispatch({ type: ACTION_SAVE_IN_PROGRESS });
      await actionSave(dispatch, state)();
    },

    [ACTION_CREATE_FORM_AND_SAVE]:
      (dispatch: Dispatch, getState: () => State) => async () => {
        const state = getState();
        recordBuilderHeaderAnalytics(state, 'save');
        dispatch({ type: ACTION_SAVE_IN_PROGRESS });
        await actionCreateFormAndSave(dispatch, state)();
      },
    [ACTION_CREATE_FORM_AND_SHARE]:
      (dispatch: Dispatch, getState: () => State) => async (action: Action) => {
        const state = getState();
        recordBuilderHeaderAnalytics(state, 'share');
        dispatch({ type: ACTION_SAVE_BEFORE_SHARE_IN_PROGRESS });
        if (await actionCreateFormAndSave(dispatch, state)()) {
          dispatch(action);
        }
      },
    [ACTION_CREATE_FORM_AND_PREVIEW]: (
      dispatch: Dispatch,
      getState: () => State,
    ) => {
      return async () => {
        const state = getState();
        const { resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formbuilderheader',
          target: 'preview',
          data: {
            form_name: selector.getTitle(state),
            form_desc: selector.getDescription(state),
            num_upload_zones: selector.getNumUploadZones(state),
            num_metadata_fields: selector.getNumMetadataFields(state),
          },
        });

        dispatch({ type: ACTION_SAVE_BEFORE_PREVIEW_IN_PROGRESS });

        const newFormFileRequestId = await actionCreateFormAndSave(
          dispatch,
          state,
        )();
        if (newFormFileRequestId) {
          // Manage the new window
          const newWindowReference = window.open(
            '',
            FORMS_BUILDER_PREVIEW_WINDOW,
          );
          if (!newWindowReference) {
            dispatch({ type: ACTION_RESET_SAVE_BEFORE_PREVIEW_IN_PROGRESS });
            return;
          }
          newWindowReference.location =
            selector.getFormPreviewUrl(newFormFileRequestId);

          newWindowReference.focus();
        } else {
          dispatch({ type: ACTION_RESET_SAVE_BEFORE_PREVIEW_IN_PROGRESS });
        }
      };
    },
    [ACTION_SAVE_AND_COPY]:
      (dispatch: Dispatch, getState: () => State) => async () => {
        // TODO - Save only if change detected
        const state = getState();

        recordBuilderHeaderAnalytics(state, 'copy');

        dispatch({ type: ACTION_SAVE_IN_PROGRESS });
        if (await actionSave(dispatch, state)()) {
          dispatch({ type: ACTION_OPEN_COPY_MODAL });
        }
      },
    [ACTION_SAVE_AND_EXIT]:
      (dispatch: Dispatch, getState: () => State) => async (action: Action) => {
        const { exitBuilder, navigation, baseUrl, mode } = action;
        // TODO - Save only if change detected
        const state = getState();
        recordBuilderHeaderAnalytics(state, 'exit');
        const actionSaveAndExit = selector.isNewForm(state, mode)
          ? actionCreateFormAndSave
          : actionSave;

        dispatch({ type: ACTION_SAVE_IN_PROGRESS });
        if (await actionSaveAndExit(dispatch, state)()) {
          exitBuilder(baseUrl, mode, state, navigation.applicationHistory);
        }
      },
    [ACTION_SAVE_AND_PREVIEW]: (dispatch: Dispatch, getState: () => State) => {
      return async () => {
        const state = getState();
        const { workingFileRequest } = state;
        const formType = get(workingFileRequest, 'type');
        const hasUnsavedChanges = selector.hasUnsavedChanges(state);

        recordBuilderHeaderAnalytics(state, 'preview');

        // If no changes to save and it's a form, open preview directly
        if (!hasUnsavedChanges && formType === MODE.FORM) {
          openPreviewWindow(formType, workingFileRequest.id, state);
          return;
        }

        // Otherwise save before preview
        dispatch({ type: ACTION_SAVE_BEFORE_PREVIEW_IN_PROGRESS });
        const successfullySaved = await actionSave(dispatch, state)();

        if (successfullySaved) {
          openPreviewWindow(formType, workingFileRequest?.id, state);
        } else {
          dispatch({ type: ACTION_RESET_SAVE_BEFORE_PREVIEW_IN_PROGRESS });
        }
      };
    },
    [ACTION_REORDER_GROUP_FIELDS]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const state = getState();
        const { resin, resinEvent } = state;
        if (resin) {
          resin.recordAction({
            ...resinEvent,
            component: 'formelement',
            target: 'fieldreorder, addnewfeild',
            data: {
              file_request_url_id: selector.getFileRequestUrlId(state),
            },
          });
        }
        dispatch(action);
      },
    [ACTION_OPEN_SHARE_MODAL]:
      (dispatch: Dispatch, getState: () => State) => async (action: Action) => {
        const state = getState();
        const { workingFileRequest } = state;
        const formType = get(workingFileRequest, 'type');
        const hasUnsavedChanges = selector.hasUnsavedChanges(state);
        recordBuilderHeaderAnalytics(state, 'share');
        // Save if needed before sharing
        if (hasUnsavedChanges || formType === MODE.FILE_REQUEST) {
          dispatch({ type: ACTION_SAVE_BEFORE_SHARE_IN_PROGRESS });
          const saveSuccessful = await actionSave(dispatch, state)();

          if (saveSuccessful) {
            dispatch(action);
          }
          // If save fails, we don't proceed with the share modal
        } else {
          dispatch(action);
        }
      },

    [ACTION_FILE_REQUEST_UPDATED_FROM_SHARE_MODAL]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_CLOSE_SHARE_MODAL]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_SET_LABEL]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_UPDATE_LIST_OPTIONS]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_UPDATE_MAXIMUM_SELECTIONS]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const { id, value } = action;
        const state = getState();
        const numOptions = selector.getOptions(state, id).length;
        const newMaxSelections = calculateNewMaxSelections(value, numOptions);
        dispatch({ ...action, value: newMaxSelections });
      },
    [ACTION_SET_LIST_OPTIONS_ERROR]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_SET_DESCRIPTION]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_SET_DESCRIPTION_EDITABLE]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const state = getState();
        const { resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formelement',
          target: `adddescription|check`,
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
        dispatch(action);
      },
    [ACTION_REMOVE_DESCRIPTION]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const state = getState();
        const { resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formelement',
          target: `adddescription|uncheck`,
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
        dispatch(action);
      },
    [ACTION_OPEN_EXIT_MODAL]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_CLOSE_EXIT_MODAL]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_DRAGGING_STARTED]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_DRAGGING_FINISHED]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_HAS_SCROLLED_TO_BOTTOM]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_CLOSE_COPY_MODAL]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_SEARCH_ITEMS]: (dispatch: Dispatch) => (action: Action) => {
      dispatch({ type: ACTION_SEARCH_ITEMS_IN_PROGRESS });
      const { query, isStartingSearch } = action;
      ItemAPI.searchItems(query, isStartingSearch).then(data => {
        dispatch({ ...action, data });
      });
    },
    [ACTION_GET_FOLDER_TREE]: (dispatch: Dispatch) => (action: Action) => {
      dispatch({ type: ACTION_GET_FOLDER_TREE_IN_PROGRESS });
      const { folderId, pageNumber } = action;
      ItemAPI.getFolderTree(folderId, pageNumber).then(data => {
        dispatch({ ...action, data });
      });
    },
    [ACTION_CLEAR_CONTENT_PICKER]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_COPY_FILE_REQUEST]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const state = getState();
        const { workingFileRequest } = state;
        dispatch({ type: ACTION_COPY_FILE_REQUEST_IN_PROGRESS });
        const { selectionItems } = action;
        FileRequestAPI.copyFileRequest(
          workingFileRequest,
          selectionItems.map(selectionItem => selectionItem.id),
        ).then(data => {
          dispatch({ ...action, data });
        });
      },
    [ACTION_OPEN_UPLOAD_LOGO_MODAL]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_CLOSE_UPLOAD_LOGO_MODAL]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_OPEN_THEME_PANEL]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_CLOSE_THEME_PANEL]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_OPEN_SETTINGS_PANEL]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_CLOSE_SETTINGS_PANEL]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_UPDATE_UPLOAD_LOGO_ITEM]:
      (dispatch: Dispatch) => async (action: Action) => {
        const { file } = action;
        if (!file) {
          // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
          dispatch({ ...action, dataUrl: null });
          return;
        }

        const { size, type } = file;
        /**
         * Dispatch error if file is larger than 1MB
         * Dispatch error if file is not png or jpg
         */
        if (!ACCEPTED_IMG_TYPES.includes(type)) {
          dispatch({ type: ACTION_UPLOAD_LOGO_IS_NOT_IMAGE });
          return;
        }
        if (size > MAX_CUSTOM_LOGO_SIZE) {
          dispatch({ type: ACTION_UPLOAD_LOGO_TOO_LARGE });
          return;
        }

        const dataUrl = await readFileAsDataURL(file);
        dispatch({ ...action, dataUrl, file });
      },
    [ACTION_UPLOAD_LOGO]:
      (dispatch: Dispatch, getState: () => State) => async (action: Action) => {
        const state = getState();
        const {
          workingFileRequest,
          uploadLogoModalState: { dataUrl, file },
          themePanelState: { stagedLogoPath },
        } = state;
        if (!file) {
          dispatch({ type: ACTION_UPLOAD_LOGO_EMPTY });
          return;
        }

        const isStagedLogo =
          stagedLogoPath !==
          get(state, 'savedFileRequest.form.content.theme.logo.key');

        const previousImagePath = isStagedLogo ? stagedLogoPath : '';

        dispatch({ type: ACTION_UPLOAD_LOGO_IN_PROGRESS });

        try {
          const { picPath } = await FileRequestAPI.uploadFileRequestLogo(
            workingFileRequest,
            file,
            previousImagePath,
          );
          dispatch({ ...action, dataUrl, path: picPath });

          dispatch({ type: ACTION_CLOSE_UPLOAD_LOGO_MODAL });
          /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
        } catch (e: any) {
          dispatch({ type: ACTION_UPLOAD_LOGO_ERROR });
        }
      },
    [ACTION_SET_UPLOADED_LOGO]:
      (dispatch: Dispatch, getState: () => State) => async (action: Action) => {
        const state = getState();
        const {
          uploadLogoModalState: { dataUrl, file },
        } = state;

        if (!file || !dataUrl) {
          dispatch({ type: ACTION_UPLOAD_LOGO_EMPTY });
          return;
        }
        dispatch({ ...action, dataUrl, path: file.name, file });
        dispatch({ type: ACTION_CLOSE_UPLOAD_LOGO_MODAL });
      },
    [ACTION_UPLOAD_LOGO_CLEAR]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_HEADER_LOGO_SELECT]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_SELECT_THEME_COLOR]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_SHOW_COLOR_PICKER]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_COLOR_PICKER_CANCEL]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_COLOR_PICKER_CHANGE]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_COLOR_PICKER_ADD]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_COLOR_PICKER_ERROR]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_DRAG_FORM_FIELD_START]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_DRAG_FORM_FIELD_END]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_DRAG_FORM_FIELD_ENTER_DROPZONE]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_DRAG_FORM_FIELD_LEFT_DROPZONE]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_STORE_INSERT_INDEX]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_STORE_FORM_ELEMENT_HEIGHT]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_SET_FORM_ELEMENT_TESTIDS]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_TOGGLE_FIELD_COLUMN]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const { id, groupId } = action;
        const state = getState();
        const { layout: formLayout } = selector.getPageInfoForGroupId(
          state,
          groupId,
        );
        const newLayout = getLayoutAfterFieldColToggle(formLayout, id);
        dispatch({
          type: ACTION_UPDATE_GROUP_LAYOUT,
          layout: newLayout,
          id: groupId,
        });
      },
    [ACTION_UPDATE_ITEM_HEIGHT_IN_LAYOUT]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const {
          id,
          groupId,
          value: { elementHeight },
        } = action;
        const state = getState();
        const prevElementHeight = get(
          state,
          `formElementsHeightState.${getFormElementKey(groupId, id)}`,
          {},
        );

        const { layout } = selector.getPageInfoForGroupId(state, groupId);
        const newLayout = cloneDeep(layout);
        // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2345
        const itemIndex = newLayout.findIndex(it => it.i === id);
        const itemOnUpdate = newLayout[itemIndex];
        if (prevElementHeight !== elementHeight) {
          dispatch({
            type: ACTION_STORE_FORM_ELEMENT_HEIGHT,
            groupId,
            id,
            value: { elementHeight },
          });
        }
        // ignore height change due to on focus border display
        if (Math.abs(elementHeight - prevElementHeight) > TOTAL_BORDER_WIDTH) {
          if (isItemInTwoColumn(itemOnUpdate)) {
            updateTwoColItemHeightInLayout(
              newLayout,
              itemIndex,
              prevElementHeight,
              elementHeight,
              groupId,
              get(state, `formElementsHeightState`, {}),
            );
          }
          dispatch({
            type: ACTION_UPDATE_GROUP_LAYOUT,
            layout: newLayout,
            id: groupId,
          });
        }
      },
    [ACTION_UPDATE_GROUP_LAYOUT]:
      (dispatch: Dispatch, getState: () => State) => (action: Action) => {
        const state = getState();
        const { resin, resinEvent } = state;
        if (resin) {
          resin.recordAction({
            ...resinEvent,
            component: 'formelement',
            target: 'draganddrop',
            data: {
              file_request_url_id: selector.getFileRequestUrlId(state),
            },
          });
        }
        dispatch(action);
      },
    [ACTION_TOOLBAR_DROPDOWN_CLOSE]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_TOOLBAR_DROPDOWN_OPEN]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_TOGGLE_SHOW_UPLOAD_FILE_DESCRIPTION]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_OPEN_SELECT_MODAL]: (dispatch: Dispatch) => (action: Action) => {
      dispatch(action);
    },
    [ACTION_SELECT_FORM_UPLOAD_FOLDER]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
    [ACTION_CLEAR_FORM_UPLOAD_FOLDER]:
      (dispatch: Dispatch) => (action: Action) => {
        dispatch(action);
      },
  };
};
